import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw, SearchX, Lock, Wifi, FileSearch } from "lucide-react";
import type { PricingSaasError } from '../types';

interface ErrorStateProps {
  error: PricingSaasError;
  onRetry?: () => void;
  onRunExtract?: () => void;
}

export const ErrorState = ({ error, onRetry, onRunExtract }: ErrorStateProps) => {
  const getErrorContent = () => {
    switch (error.type) {
      case 'NO_SCRAPES':
        return {
          title: 'No Screenshots Available',
          description: error.message || 'This pricing page needs to be scraped before pricing information can be extracted.',
          icon: <FileSearch className="h-16 w-16 text-blue-400" />,
          showRetry: false,
          showExtract: false,
        };

      case 'NOT_FOUND':
        return {
          title: 'No Pricing Data',
          description: error.message || 'Pricing data has not been extracted yet. Run an extract to generate pricing information.',
          icon: <FileSearch className="h-16 w-16 text-gray-400" />,
          showRetry: false,
          showExtract: true,
        };

      case 'AUTH_ERROR':
        return {
          title: 'Authentication Error',
          description: error.message || 'There was an issue with the API authentication. Please contact an administrator.',
          icon: <Lock className="h-16 w-16 text-red-400" />,
          showRetry: false,
          showExtract: false,
        };

      case 'NETWORK_ERROR':
        return {
          title: 'Connection Issue',
          description: error.message || 'Unable to fetch pricing data. Please check your connection and try again.',
          icon: <Wifi className="h-16 w-16 text-orange-400" />,
          showRetry: true,
          showExtract: false,
        };

      case 'PARSE_ERROR':
        return {
          title: 'Data Error',
          description: error.message || 'The pricing data received was invalid or incomplete.',
          icon: <AlertCircle className="h-16 w-16 text-yellow-400" />,
          showRetry: true,
          showExtract: true,
        };

      default:
        return {
          title: 'Something Went Wrong',
          description: error.message || 'An unexpected error occurred while fetching pricing data.',
          icon: <AlertCircle className="h-16 w-16 text-red-400" />,
          showRetry: true,
          showExtract: true,
        };
    }
  };

  const { title, description, icon, showRetry, showExtract } = getErrorContent();

  return (
    <div className="flex items-center justify-center py-20">
      <div className="max-w-md text-center">
        <div className="flex justify-center mb-6">
          {icon}
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3">
          {title}
        </h3>
        <p className="text-gray-600 mb-6">
          {description}
        </p>
        {error.status && (
          <div className="text-sm text-gray-400 mb-6">
            Error code: {error.status}
          </div>
        )}
        <div className="flex items-center justify-center gap-3">
          {showRetry && onRetry && (
            <Button
              onClick={onRetry}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3"
              size="lg"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          )}
          {showExtract && onRunExtract && (
            <Button
              onClick={onRunExtract}
              variant="outline"
              size="lg"
              className="px-6 py-3"
            >
              <FileSearch className="h-4 w-4 mr-2" />
              Run Extract
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
